/*
  Bello — buy-box cart grouping (Ajuste 2).

  The live cart is the Kaktus drawer (a third-party widget, sometimes in an iframe), not
  the theme's cart-drawer.liquid — so this runs in JS. Every buy-box line carries a
  `_pmd_bundle` role property (main | capsule | capsule-sub | filter) from the cart adapter.
  Capsule and subscription-capsule lines are collapsed into two <details> dropdowns under
  the main product; the filter stays its own line.

  Role is resolved DETERMINISTICALLY from the cart JSON (same variant-unique-then-cart-order
  pairing bello-cart-freq.js uses) and STAMPED onto each node once, while the DOM is still in
  natural cart order. Grouping then reads the stamp — never the DOM text — so it can't be
  corrupted by other scripts (e.g. cart-freq) that relabel lines after we reorder.

  Also: nothing is hidden unless moved into a group; empty group boxes are swept every pass;
  deleting the main product removes the rest of the build, but only on the real present ->
  absent transition so it never fires mid-add.
*/
(function () {
  'use strict';

  var ROLE_PROP = '_pmd_bundle';
  var CONTAINERS = ['.kaktusc-cart__products', '#CartDrawer-CartItems', '#main-cart-items', '.cart-items'];
  var LINE_SEL = '.kaktusc-cart__product, .cart-item, li.cart__item, tr.cart-item';
  var GROUPS = [
    { role: 'capsule', title: 'Capsules included today' },
    { role: 'capsule-sub', title: 'Subscription capsules' }
  ];

  /* The drawer re-renders its whole product list on every quantity change, which destroys
     these <details> and takes the shopper's open/closed choice with it. Remembered here and
     re-applied when the box is rebuilt, so changing a quantity no longer snaps the groups
     shut. Closed is the first-render default, as drawn. */
  var groupOpen = { 'capsule': false, 'capsule-sub': false };

  var cartItems = [];       // ordered, from /cart.js
  var cartFetchedAt = 0;
  var fetching = false;
  var pruning = false;
  var prevHadMain = null;

  /* Variants pruned from the cart whose nodes may still be on screen: variant id ->
     { until, allow }, where `allow` is how many lines the cart still holds for it. A shopper
     can hold the same capsule twice, once inside the build and once loose, and only the
     build's line goes; `allow` is what keeps the loose one on screen. Refreshed from the
     cart on every poll, and the deadline is a backstop for a drawer that never redraws. */
  var pendingDrop = {};

  /* Whether the drawer was showing the main product last pass. The prune used to wait for
     the next poll of /cart.js, up to a second and a half after the click, and the build sat
     there in the meantime. The DOM says it sooner. */
  var domHadMain = false;

  function refreshCart() {
    if (fetching || Date.now() - cartFetchedAt < 800) return;
    fetching = true;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        cartItems = (cart && cart.items) || [];
        cartFetchedAt = Date.now();
        for (var vid in pendingDrop) pendingDrop[vid].allow = countLines(cartItems, vid);
        pruneDeletedBuild(cartItems);
      })
      .catch(function () {})
      .then(function () { fetching = false; applyAll(); });
  }

  /* Option 1: deleting the main product removes the rest of its build. Fires only on the
     present -> absent transition, so adding a build never triggers it. */
  function pruneDeletedBuild(items) {
    var hasMain = items.some(function (it) { return roleOfItem(it) === 'main'; });
    var others = items.filter(function (it) {
      var role = roleOfItem(it);
      return role && role !== 'main';
    });
    var mainJustRemoved = prevHadMain === true && !hasMain && others.length > 0;
    prevHadMain = hasMain;
    if (!mainJustRemoved || pruning) return;
    var updates = {};
    others.forEach(function (it) { updates[it.key] = 0; });
    pruning = true;
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        /* The lines are gone from the cart, but the drawer is a third-party widget that
           re-renders on its own schedule and does not listen for cart:refresh, so it keeps
           showing them until something else makes it redraw — the build looked deleted on a
           reload and present until then. Take the nodes out here, and keep taking them out
           for a while: the drawer repaints after the delete, and those fresh nodes arrive
           before the next pass has stamped them.

           update.js answers with the cart, so how many lines survive for each variant is read
           straight off the response. A capsule the shopper also holds loose keeps its line;
           only the surplus nodes go, which is what the build left behind. */
        var left = (cart && cart.items) || [];
        var deadline = Date.now() + 8000;
        others.forEach(function (it) {
          var vid = String(it.variant_id);
          pendingDrop[vid] = { until: deadline, allow: countLines(left, vid) };
        });
        eachContainer(dropPendingNodes);
        cartFetchedAt = 0;
        window.dispatchEvent(new Event('cart:refresh'));
      })
      .catch(function () {})
      .then(function () { pruning = false; });
  }

  function countLines(items, vid) {
    var n = 0;
    items.forEach(function (it) { if (String(it.variant_id) === vid) n++; });
    return n;
  }

  /* Removes the lines still standing for a pruned variant. The stamp is not required to go:
     the drawer repaints right after the delete, and a fresh node carries no stamp until the
     next pass, which is exactly the window the shopper was seeing the dead capsules in. What
     is kept is the count the cart still reports for that variant — `allow`, recomputed on
     every poll — so a capsule held both inside the build and loose keeps its loose line.

     Which node goes matters when the two are on screen together: a stamped build line is
     dropped before an unstamped one, so the survivor is the loose line rather than whichever
     the drawer happened to draw first. A stamped main line is never touched. */
  function dropPendingNodes(container) {
    var now = Date.now();
    var live = false;
    for (var id in pendingDrop) {
      if (pendingDrop[id].until > now) live = true;
      else delete pendingDrop[id];
    }
    if (!live) return;

    var byVariant = {};
    lineNodes(container).forEach(function (node) {
      if (node.getAttribute('data-bbb-role') === 'main') return;
      var vid = variantIdOf(node);
      if (!vid || !pendingDrop[vid]) return;
      (byVariant[vid] = byVariant[vid] || []).push(node);
    });

    Object.keys(byVariant).forEach(function (vid) {
      var nodes = byVariant[vid];
      var surplus = nodes.length - pendingDrop[vid].allow;
      if (surplus <= 0) return;
      // Stamped build lines first, then the rest in the order the drawer drew them.
      nodes.sort(function (a, b) {
        return (b.getAttribute('data-bbb-role') ? 1 : 0) - (a.getAttribute('data-bbb-role') ? 1 : 0);
      });
      nodes.slice(0, surplus).forEach(function (node) { node.remove(); });
    });

    sweepEmptyGroups(container);
  }

  function roleOfItem(it) {
    return it && it.properties && it.properties[ROLE_PROP];
  }

  function variantIdOf(node) {
    var id = node.getAttribute('data-id') || node.getAttribute('data-variant-id');
    if (id) return String(id).replace(/\D/g, '');
    var input = node.querySelector('input[name^="updates"], input[data-quantity-variant-id], [data-variant-id]');
    if (input) {
      var n = input.getAttribute('data-quantity-variant-id') || input.getAttribute('data-variant-id');
      if (n) return String(n).replace(/\D/g, '');
      var m = (input.getAttribute('name') || '').match(/updates\[(\d+)\]/);
      if (m) return m[1];
    }
    return '';
  }

  function lineNodes(container) {
    return Array.prototype.slice.call(container.querySelectorAll(LINE_SEL));
  }

  /* Pair each line node to a distinct cart item — exact variant match wins, leftovers pair
     in cart order (mirrors bello-cart-freq's mapLinesToItems). Requires the nodes to be in
     natural cart order, so it is only run before any reordering. Stamps data-bbb-role. */
  function stampRoles(nodes) {
    var items = cartItems;
    if (!items.length) return;
    var taken = [];
    var assigned = [];

    nodes.forEach(function (el, i) {
      var did = variantIdOf(el);
      if (!did) return;
      var found = -1, hits = 0;
      for (var j = 0; j < items.length; j++) {
        if (taken[j]) continue;
        var it = items[j];
        if (String(it.variant_id) === did || String(it.id) === did || String(it.key) === did) { found = j; hits++; }
      }
      if (hits === 1) { assigned[i] = items[found]; taken[found] = true; }
    });
    var next = 0;
    for (var i = 0; i < nodes.length; i++) {
      if (assigned[i]) continue;
      while (next < items.length && taken[next]) next++;
      if (next >= items.length) break;
      assigned[i] = items[next];
      taken[next] = true;
    }
    nodes.forEach(function (el, i) {
      var role = assigned[i] ? roleOfItem(assigned[i]) : '';
      el.setAttribute('data-bbb-role', role || '');
    });
  }

  function ensureStyle(doc) {
    if (doc.getElementById('bbb-cart-group-style')) return;
    var style = doc.createElement('style');
    style.id = 'bbb-cart-group-style';
    style.textContent =
      '.bbb-cart-group{border-top:1px solid rgba(0,0,0,.08);margin-top:8px;padding-top:8px}' +
      '.bbb-cart-group__summary{cursor:pointer;list-style:none;font-size:13px;font-weight:600;color:#3b382b;display:flex;align-items:center;padding:4px 0}' +
      '.bbb-cart-group__summary::-webkit-details-marker{display:none}' +
      '.bbb-cart-group__summary::after{content:"";width:7px;height:7px;margin-left:auto;border-right:1.5px solid currentColor;border-bottom:1.5px solid currentColor;transform:rotate(45deg);transition:transform .2s ease}' +
      '.bbb-cart-group[open] .bbb-cart-group__summary::after{transform:rotate(-135deg)}' +
      '@media (prefers-reduced-motion:reduce){.bbb-cart-group__summary::after{transition:none}}';
    (doc.head || doc.body || doc.documentElement).appendChild(style);
  }

  function groupTitle(role) {
    for (var i = 0; i < GROUPS.length; i++) if (GROUPS[i].role === role) return GROUPS[i].title;
    return role;
  }
  function ensureGroupBox(container, doc, role) {
    var box = container.querySelector('[data-bbb-cart-group="' + role + '"]');
    if (box) return box;
    box = doc.createElement('details');
    box.className = 'bbb-cart-group';
    box.setAttribute('data-bbb-cart-group', role);
    if (groupOpen[role]) box.open = true;
    box.addEventListener('toggle', function () { groupOpen[role] = box.open; });
    var summary = doc.createElement('summary');
    summary.className = 'bbb-cart-group__summary';
    summary.textContent = groupTitle(role);
    box.appendChild(summary);
    container.appendChild(box);
    return box;
  }
  function sweepEmptyGroups(container) {
    container.querySelectorAll('[data-bbb-cart-group]').forEach(function (box) {
      if (!box.querySelector(LINE_SEL)) box.remove();
    });
  }

  function applyIn(container) {
    var doc = container.ownerDocument;
    dropPendingNodes(container);
    sweepEmptyGroups(container);
    var nodes = lineNodes(container);
    if (!nodes.length) return;

    // Stamp roles only while the DOM is still in natural cart order — i.e. before we have
    // moved anything into a group this render. Once stamped, the stamp is authoritative.
    var anyGrouped = nodes.some(function (n) { return n.closest('[data-bbb-cart-group]'); });
    var anyUnstamped = nodes.some(function (n) { return !n.hasAttribute('data-bbb-role'); });
    if (!anyGrouped && anyUnstamped) stampRoles(nodes);

    if (!nodes.some(function (n) { return n.getAttribute('data-bbb-role'); })) { sweepEmptyGroups(container); return; }
    ensureStyle(doc);

    nodes.forEach(function (n) {
      var role = n.getAttribute('data-bbb-role');
      var desired = (role === 'capsule' || role === 'capsule-sub') ? role : null;
      var currentBox = n.closest('[data-bbb-cart-group]');
      var currentRole = currentBox ? currentBox.getAttribute('data-bbb-cart-group') : null;
      if (desired === currentRole) return;
      if (!desired) { if (currentBox) container.insertBefore(n, currentBox); return; }
      ensureGroupBox(container, doc, desired).appendChild(n);
    });

    sweepEmptyGroups(container);
  }

  function accessibleDocs() {
    var docs = [document];
    document.querySelectorAll('iframe').forEach(function (f) {
      try { if (f.contentDocument && f.contentDocument.querySelector) docs.push(f.contentDocument); } catch (e) {}
    });
    return docs;
  }
  function eachContainer(fn) {
    try {
      accessibleDocs().forEach(function (doc) {
        CONTAINERS.forEach(function (sel) { doc.querySelectorAll(sel).forEach(fn); });
      });
    } catch (e) {}
  }
  function applyAll() {
    eachContainer(applyIn);

    /* The main line gone while the rest of the build is still on screen means the shopper
       just deleted the pack: fetch the cart now rather than on the next tick, so the prune
       and the node removal follow the click closely. Fired once per disappearance. */
    var hasMain = false;
    var hasOther = false;
    eachContainer(function (container) {
      lineNodes(container).forEach(function (node) {
        var role = node.getAttribute('data-bbb-role');
        if (role === 'main') hasMain = true;
        else if (role) hasOther = true;
      });
    });
    if (domHadMain && !hasMain && hasOther) {
      domHadMain = false;
      cartFetchedAt = 0;
      refreshCart();
    } else if (hasMain) {
      domHadMain = true;
    }
  }

  function tick() { observeDocs(); refreshCart(); applyAll(); }
  ['cart:refresh', 'cart:updated', 'pageshow'].forEach(function (name) {
    window.addEventListener(name, function () { cartFetchedAt = 0; tick(); });
  });
  /* The drawer lives in an iframe, and an observer on this document never sees it repaint,
     so the groups only caught up on the next interval — a second of dead capsules on screen
     after a delete. Each reachable frame gets its own observer, attached once. */
  var observed = [];
  function observeDocs() {
    if (!window.MutationObserver) return;
    accessibleDocs().forEach(function (doc) {
      if (!doc.documentElement || observed.indexOf(doc) !== -1) return;
      observed.push(doc);
      new MutationObserver(function () { applyAll(); }).observe(doc.documentElement, { childList: true, subtree: true });
    });
  }
  observeDocs();
  refreshCart();
  setInterval(tick, 1500);
})();
